import { ReactNode } from "react";

import logo from "../images/logo.png";
import messages from "../messages/ja.json";
import { FeVersion, feVersionLabel } from "./fe-version";

import "../styles/auth-flow.css";

export interface WizardStep {
  label: string;
  state: "active" | "done" | "todo";
}

interface Props {
  // Small label on top (e.g. "TeamSpirit MFA"). Rendered inside the card. Omit if not needed.
  kicker?: string;
  // Large page title rendered above the card, next to the brand logo (e.g. "MFA").
  title?: string;
  // Full-width notice strip at the top of the fullscreen shell (e.g. first-time setup tip).
  banner?: ReactNode;
  // Step-pill row (stepped login). Omit for single-step pages.
  steps?: WizardStep[];
  // true (default): full screen with gradient background + brand + footer.
  // false: the card alone, for a host that already provides the page chrome. No caller
  // today — the Layout that used it is gone, so every screen is the fullscreen shell.
  fullscreen?: boolean;
  // Widens the card from the default 530px to 800px (Figma) for form-heavy pages such as
  // secret-question enrollment.
  wide?: boolean;
  // Extra class on the fullscreen shell so a single page can scope its own overrides
  // (e.g. the login screen's darker title) without touching the shared auth styles.
  className?: string;
  children: ReactNode;
}

// Shared layout wrapper for the auth flow: brand header + card panel + footer on a
// light-blue gradient background. Styling lives in styles/auth-flow.css.
export function AuthShell({
  kicker,
  title,
  banner,
  steps,
  fullscreen = true,
  wide = false,
  className,
  children,
}: Props) {
  const panel = (
    <section className="content-panel">
      {kicker ? <p className="content-kicker">{kicker}</p> : null}
      {steps && steps.length > 0 ? (
        <div className="step-pill-row">
          {steps.map((step, index) => (
            <div
              key={index}
              className={`step-pill ${
                step.state === "active" ? "is-active" : step.state === "done" ? "is-done" : ""
              }`}
            >
              {step.label}
            </div>
          ))}
        </div>
      ) : null}
      {children}
    </section>
  );

  if (!fullscreen) {
    return <div className={`auth-embedded${wide ? " is-wide" : ""}`}>{panel}</div>;
  }

  return (
    <main className={`app-shell${wide ? " is-wide" : ""}${className ? ` ${className}` : ""}`}>
      {banner}
      {/* In flow rather than absolutely positioned, so it lands under the banner strip
          instead of on top of it. Skipped entirely when there is no version, so an empty
          row can't push the page down. */}
      {feVersionLabel ? (
        <div className="auth-topbar">
          <FeVersion />
        </div>
      ) : null}
      <div className="auth-main">
        <div className="auth-head">
          <div className="auth-logo">
            <img src={logo} alt={messages.app.brand} className="auth-logo-mark" />
          </div>
          {title ? <h1 className="auth-title">{title}</h1> : null}
        </div>

        {panel}
      </div>
    </main>
  );
}
